import { z } from 'zod';
import { ReferralPatientSchema } from '../ReferralPatientSchema';
import { IObject } from '../../IObject';
import { ReferralBaseSchema } from '../ReferralBaseSchema';
import { ReferrerSchema } from '../ReferrerSchema';
import { OrganizationSchema } from '../../OrganizationSchema';
import { ReferralGenericDocumentsSchema } from './ReferralGenericDocumentsSchema';
import { ReferralCoverageSchema } from '../ReferralCoverageSchema';
import { ReferralDiagnosisSchema } from '../ReferralDiagnosisSchema';
import {
  ReferralConsentRequestSchema,
  ReferralConsentSchema,
  requiresPatientPhoneForConsent,
} from '../ReferralConsentSchema';
import { ReferralNotesSchema } from '../ReferralNotesSchema';
import { allJoinReasonIds } from '../../../data/joinReasons';
import { joinReasonIdSchema } from '../ReferralJoinReasonSchema';
import { requiredString } from '../../../validation';
import { withLegacyPlanIdentifiers } from '../hoistPlanIdentifiersToPatient';

const ReferralBaseGenericSchemaObject = {
  // ID is required for L&G referrals to be passed from UI
  // because a referrer uploads documents to the storage "referrals/{generated-referral-id}/document.pdf"
  id: z.string().min(1),
  patient: ReferralPatientSchema,
  coverage: ReferralCoverageSchema,
  diagnosis: ReferralDiagnosisSchema,
  joinReasonId: joinReasonIdSchema(allJoinReasonIds),
  referrer: ReferrerSchema,
  // gip: z.object({
  //   code: requiredString('GIP code is required'),
  // }),
  documents: ReferralGenericDocumentsSchema,
  // Optional free text the referrer typed on the referral form. Internal
  // only: it is never shown to the member (see ReferralNotesSchema)
  notes: ReferralNotesSchema,
  // How we know the patient consented to being referred. The REQUEST form:
  // a body states the EVIDENCE only — the status and what we record when we
  // ask are ours to write (see ReferralConsentSchema; the stored schema
  // below carries the full shape)
  consent: ReferralConsentRequestSchema,
};

export const ReferralCreateGenericSchema = withLegacyPlanIdentifiers(
  z
    .object({
      ...ReferralBaseGenericSchemaObject,
      organizationId: requiredString('Organisation is required'),
    })
    .check(requiresPatientPhoneForConsent),
);

// As it is stored in the database
export const ReferralGenericSchema = ReferralBaseSchema.extend({
  ...ReferralBaseGenericSchemaObject,
  // as stored: the evidence and status the body stated, plus whatever we
  // recorded when we asked the patient ourselves
  consent: ReferralConsentSchema,
  organization: OrganizationSchema,
});

export type ReferralCreateGeneric = z.infer<typeof ReferralCreateGenericSchema>;
export type ReferralGenericData = z.infer<typeof ReferralGenericSchema>;
export type ReferralGeneric = IObject.HasIdTimestamped<ReferralGenericData>;
